// src/phase4/types/enemy.ts
// Enemy-related type definitions for Phase 4 tower defense system

import type { TowerType } from './tower';

/** Enemy type enumeration */
export enum EnemyType {
  SQL_INJECTION = 'SQL_INJECTION',
  XSS_ATTACK = 'XSS_ATTACK',
  DDOS_BOT = 'DDOS_BOT',
  TROJAN = 'TROJAN',
  ZERO_DAY = 'ZERO_DAY',
}

/** Special ability that enemies can have */
export type EnemySpecialAbility =
  | 'stealth'       // Invisible to towers until attacked
  | 'shield'        // Shield absorbing damage
  | 'speed_boost'   // Temporary speed increase
  | 'spawn_clone';  // Spawn clones on death

/** Special ability configuration */
export interface EnemySpecialAbilityConfig {
  type: EnemySpecialAbility;
  triggerHpPercent?: number; // At what HP% the ability triggers
  effectDuration?: number;   // How long the effect lasts (ms)
  effectValue?: number;      // Effect magnitude (e.g., shield amount)
}

/** Enemy type configuration (static data) */
export interface EnemyTypeConfig {
  type: EnemyType;
  name: string;
  baseHp: number;
  baseSpeed: number;
  bounty: number; // FLOP reward on kill
  size: number;   // Visual size for collision
  specialAbility?: EnemySpecialAbilityConfig;
  // Resource paths
  textureKey: string;
  frameCount?: number; // Animation frames if applicable
}

/** Enemy special ability state (runtime) */
export interface EnemyAbilityState {
  type: EnemySpecialAbility;
  isActive: boolean;
  triggerValue?: number; // e.g., remaining shield amount
  expiresAt?: number;
}

/** Runtime enemy instance state */
export interface EnemyInstance {
  id: string;
  type: EnemyType;
  maxHp: number;
  currentHp: number;
  speed: number;
  x: number;
  y: number;
  pathProgress: number; // 0 to 1 along path
  abilityState?: EnemyAbilityState;
  isDead: boolean;
  isStealthed: boolean;
}

/** Damage info for applying damage to enemies */
export interface DamageInfo {
  amount: number;
  isCritical: boolean;
  towerType: TowerType;
  hasSlowEffect: boolean;
  hasPierceEffect: boolean;
  hasAoeEffect: boolean;
}
